import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useUser } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { useApiAuth } from "./useApiAuth";
import { apiUrl } from "../lib/api";
import { canonicalRoundDisplayName } from "@sportex100/shared/utils/competitionIdentity.ts";

export type CompetitionRound = {
  round_code: string;
  round_name: string;
  round_sequence: number;
  wins_required: number;
  series_format_name: string;
};

export type CompetitionRoundsData = {
  competition_code: string;
  rounds: CompetitionRound[];
};

export type UseCompetitionRoundsResult = {
  rounds: CompetitionRound[];
  roundLabels: Record<string, string>;
  winsRequired: Record<string, number>;
  loading: boolean;
  error: string | null;
};

type CompetitionRoundsContextValue = UseCompetitionRoundsResult & {
  competitionCode: string;
};

const CompetitionRoundsContext = createContext<CompetitionRoundsContextValue | null>(null);

const buildRoundMaps = (rounds: CompetitionRound[]) => {
  const labels: Record<string, string> = {};
  const wins: Record<string, number> = {};
  rounds.forEach((round) => {
    labels[round.round_code] = round.round_name;
    wins[round.round_code] = round.wins_required;
  });
  return { labels, wins };
};

export function CompetitionRoundsProvider({
  competitionCode,
  children,
}: {
  competitionCode: string;
  children: ReactNode;
}) {
  const { acquireToken } = useApiAuth();
  const { isSignedIn: isAuthenticated } = useUser();
  const canLoad = Boolean(competitionCode && isAuthenticated);
  const query = useQuery({
    queryKey: ["competition-rounds", competitionCode],
    enabled: canLoad,
    queryFn: async () => {
      const token = await acquireToken();
      if (!token) {
        throw new Error("Failed to acquire authentication token");
      }

      const response = await fetch(apiUrl(`/api/competitions/${competitionCode}/rounds`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch rounds: ${response.statusText}`);
      }

      const data: CompetitionRoundsData = await response.json();
      return data.rounds.map((round) => ({
        ...round,
        round_name: canonicalRoundDisplayName(round.round_code, round.round_name) ?? round.round_name,
      }));
    },
  });

  const rounds = canLoad ? query.data ?? [] : [];
  const { labels: roundLabels, wins: winsRequired } = useMemo(
    () => buildRoundMaps(rounds),
    [rounds],
  );
  const loading = canLoad ? query.isLoading : false;
  const error = canLoad && query.error ? (query.error as Error).message : null;

  const value = useMemo(
    () => ({ competitionCode, rounds, roundLabels, winsRequired, loading, error }),
    [competitionCode, rounds, roundLabels, winsRequired, loading, error]
  );

  return (
    <CompetitionRoundsContext.Provider value={value}>
      {children}
    </CompetitionRoundsContext.Provider>
  );
}

export function useCompetitionRounds(): CompetitionRoundsContextValue {
  const context = useContext(CompetitionRoundsContext);
  if (!context) {
    throw new Error("useCompetitionRounds must be used within CompetitionRoundsProvider");
  }
  return context;
}
